import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import useOrgDataStore from '@/stores/org-store';
import { useCreateSurvey } from '@/hooks/useSurveyQueries';
import { useSurveyIndicators } from '@/hooks/useSurveyQueries';
import { getCurrentPeriod, getCurrentYear } from '@/utils/periodUtils';
import type { Indicator, CreateSurveyRequest } from '@/types/survey';

interface FormData {
  name: string;
}

interface FormErrors {
  name?: string;
}

const CreatePresetAssessmentPage = () => {
  const navigate = useNavigate();
  const { selectedSchool, selectedDistrict } = useOrgDataStore();
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch indicators to get the preset indicators
  const { data: indicatorsResponse, isLoading: indicatorsLoading } = useSurveyIndicators(
    true, 
    false, 
    selectedDistrict?.orgId
  );

  const indicators: Indicator[] = indicatorsResponse?.indicators || [];
  const presetIndicators = selectedSchool?.presetIndicators || [];
  
  // Filter indicators to only show those with appliedTo: 'adult' and that are in presetIndicators
  const selectedIndicators = indicators.filter(indicator => 
    indicator.appliesTo.includes('adult') && 
    presetIndicators.includes(indicator.indicatorId)
  );

  // Create survey mutation
  const createSurveyMutation = useCreateSurvey({
    onSuccess: () => {
      toast.success('Assessment created successfully');
      navigate('/assessment');
    },
    onError: (error) => {
      console.error('Failed to create survey:', error);
      toast.error('Failed to create assessment');
    }
  });

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Assessment name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!selectedSchool || !selectedDistrict) return;

    setIsSubmitting(true);

    try {
      const surveyData: CreateSurveyRequest = {
        surveyName: formData.name,
        orgId: selectedSchool.orgId || '',
        survey: {
          indicators: selectedIndicators.map(indicator => indicator.indicatorId),
          quarter: getCurrentPeriod(),
          year: parseInt(getCurrentYear()),
          appliedTo: 'adult',
          designatedRecipient: ['support+priority@terracemetrics.org'],
          wellnessCenterCheck: true,
          fixedOrder: false,
          selfRegistration: true
        },
        participants: [],
        consentType: 'passive',
        consentTemplate: {
          subject: 'Assessment Participation',
          body: 'By participating in this assessment, you consent to the use of your responses for research and improvement purposes.'
        }
      };

      await createSurveyMutation.mutateAsync(surveyData);
    } catch (error) {
      console.error('Error creating assessment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Redirect if no preset indicators
  useEffect(() => {
    if (selectedSchool && (!selectedSchool.presetIndicators || selectedSchool.presetIndicators.length === 0)) {
      navigate('/assessment/create');
    }
  }, [selectedSchool, navigate]);

  if (indicatorsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!selectedSchool || !selectedDistrict) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No School Selected</h2>
          <p className="text-gray-600 mb-4">Please select a school to create an assessment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 max-w-2xl">
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-2xl">Create Assessment</CardTitle>
          <CardDescription className="text-base">
            This assessment is managed by Terrace Metrics. Please enter a name for this assessment.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Assessment Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Assessment Name</label>
              <Input
                placeholder="Enter assessment name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
              {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
            </div>

            {/* Preset Indicators Summary */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Preset Indicators</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {selectedIndicators.map((indicator) => (
                  <div
                    key={indicator.indicatorId}
                    className="p-2 rounded border bg-gray-50 text-sm"
                  >
                    {indicator.displayAs || indicator.indicator}
                    {indicator.variant && indicator.variant !== 'default' && ` (${indicator.variant})`}
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                {selectedIndicators.length} indicators will be included in this assessment.
              </p>
            </div>


            {/* Create Button */}
            <div className="flex justify-end pt-6">
              <Button
                type="submit"
                disabled={isSubmitting || !formData.name.trim()}
                className="px-8"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Assessment
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreatePresetAssessmentPage;
